import { create } from "zustand";
import {
  deleteNotification,
  fetchNotifications,
  fetchUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../Apis/Notifications-api";

const useNotificationStore = create((set, get) => ({
  items: [],
  unreadCount: 0,
  loading: false,
  activeCategory: "all",
  organizationId: null,

  setOrganizationId: (organizationId) => set({ organizationId }),

  setActiveCategory: (activeCategory) => set({ activeCategory }),

  setUnreadCount: (unreadCount) =>
    set({ unreadCount: Math.max(0, Number(unreadCount) || 0) }),

  prependNotification: (notification) => {
    if (!notification?.notification_id) return;
    set((state) => {
      const exists = state.items.some(
        (n) => n.notification_id === notification.notification_id
      );
      const items = exists
        ? state.items.map((n) =>
            n.notification_id === notification.notification_id
              ? { ...n, ...notification }
              : n
          )
        : [notification, ...state.items];
      const unreadBump =
        !exists && !notification.is_read ? 1 : 0;
      return {
        items,
        unreadCount: state.unreadCount + unreadBump,
      };
    });
  },

  loadUnreadCount: async (organizationId) => {
    if (!organizationId) return 0;
    try {
      const count = await fetchUnreadNotificationCount(organizationId);
      set({ unreadCount: count, organizationId });
      return count;
    } catch {
      return get().unreadCount;
    }
  },

  loadNotifications: async (organizationId, category = "all") => {
    if (!organizationId) return;
    set({ loading: true, organizationId, activeCategory: category });
    try {
      const response = await fetchNotifications({
        organizationId,
        category,
      });
      set({
        items: Array.isArray(response?.data) ? response.data : [],
        unreadCount:
          typeof response?.unread_count === "number"
            ? response.unread_count
            : get().unreadCount,
        loading: false,
      });
    } catch {
      set({ loading: false });
    }
  },

  markOneRead: async (notificationId) => {
    const { organizationId } = get();
    if (!organizationId || !notificationId) return;
    try {
      const response = await markNotificationRead(notificationId, organizationId);
      set((state) => ({
        items: state.items.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        ),
        unreadCount:
          typeof response?.unread_count === "number"
            ? response.unread_count
            : Math.max(0, state.unreadCount - 1),
      }));
    } catch {
      /* ignore */
    }
  },

  markAllRead: async (category = null) => {
    const { organizationId } = get();
    if (!organizationId) return;
    try {
      const response = await markAllNotificationsRead(organizationId, category);
      set((state) => ({
        items: state.items.map((n) => {
          if (category && category !== "all" && n.category !== category) {
            return n;
          }
          return { ...n, is_read: true };
        }),
        unreadCount:
          typeof response?.unread_count === "number" ? response.unread_count : 0,
      }));
    } catch {
      /* ignore */
    }
  },

  removeOne: async (notificationId) => {
    const { organizationId, items } = get();
    if (!organizationId || !notificationId) return;
    const target = items.find((n) => n.notification_id === notificationId);
    try {
      const response = await deleteNotification(notificationId, organizationId);
      set((state) => ({
        items: state.items.filter((n) => n.notification_id !== notificationId),
        unreadCount:
          typeof response?.unread_count === "number"
            ? response.unread_count
            : target && !target.is_read
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount,
      }));
    } catch {
      /* ignore */
    }
  },
}));

export default useNotificationStore;
